import {
  CREATE_PASSWORD_REQUEST,
  CREATE_PASSWORD_SUCCESS,
  CREATE_PASSWORD_FAIL,
} from "../constants/createPasswordConstants.js";

import UserService from "../../services/userService.js";
import TokenService from "../../services/tokenService.js";
import { toast } from "../../utils/toast";

/**
 * Securely store user data to localStorage with token management
 * @param {Object} responseData
 */
const pushToLocalStorage = (responseData) => {
  try {
    const actualData = responseData.data || responseData;
    
    console.log('Storing user data:', actualData);
    if (actualData.token) {
      if (actualData.expires_in) {
        TokenService.setToken(
          actualData.token, 
          actualData.refresh_token, 
          actualData.expires_in
        );
      } else {
        TokenService.setSanctumToken(actualData.token);
      }
    }

    const storageItems = {
      userInfo: actualData.patient,
      hospital: actualData.hospital?.hospital_id || actualData.hospital_id,
      hospital_name: actualData.hospital?.system_name || actualData.hospital_name,
      hospital_phone: actualData.hospital?.phone || actualData.hospital_phone,
      type: actualData.type,
      consultation_audio: actualData.hospital?.consultation_audio || actualData.consultation_audio,
      consultation_video: actualData.hospital?.consultation_video || actualData.consultation_video,
      hospital_primary_color: actualData.hospital?.primary_color || actualData.primary_color,
      hospital_secondary_color: actualData.hospital?.secondary_color || actualData.secondary_color,
      hospital_opaque_color: actualData.hospital?.opaque_color || actualData.opaque_color,
    };

    // Store each item in localStorage (excluding sensitive token data)
    Object.entries(storageItems).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        localStorage.setItem(key, JSON.stringify(value));
      }
    });
    
    console.log('User data stored securely');
    
  } catch (error) {
    console.error('Error saving to localStorage:', error);
    toast.error('Failed to save user data locally');
  }
};

const hexToRgb = (hex) => {
  if (!hex) return null;
  let h = hex.replace('#', '');
  if (h.length === 3) h = h[0]+h[0]+h[1]+h[1]+h[2]+h[2];
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return `${r} ${g} ${b}`;
};

const applyThemeDirectly = (colors) => {
  if (!colors) return;
  const root = document.documentElement;
  if (colors.primary_color) {
    root.style.setProperty('--color-primary', hexToRgb(colors.primary_color));
  }
  if (colors.secondary_color) {
    root.style.setProperty('--color-secondary', hexToRgb(colors.secondary_color));
  }
  if (colors.opaque_color) {
    root.style.setProperty('--color-opaque', hexToRgb(colors.opaque_color));
  }
};

const createPasswordAction = (phoneNumber, fileNumber, password, confirmPassword) => async (dispatch) => {
  try {
    dispatch({
      type: CREATE_PASSWORD_REQUEST,
    });

    const data = await UserService.createPassword(phoneNumber, fileNumber, password, confirmPassword);

    const actualData = data.data || data;
    if (actualData.hospital) {
      applyThemeDirectly(actualData.hospital);
    }

    dispatch({
      type: CREATE_PASSWORD_SUCCESS,
      payload: data,
    });

    // Save user data to localStorage
    pushToLocalStorage(data);

    console.log('Password created successfully:', data);
    toast.success('Password created successfully! You are now logged in.');
    
  } catch (error) {
    console.error('Create password error:', error);
    
    // Handle different error response structures
    let errorMessage = 'Failed to create password';
    
    if (error.response?.data) {
      const { message, errors } = error.response.data;
      
      if (message && errors && Array.isArray(errors) && errors.length > 0) {
        // Laravel validation errors with message and errors array
        errorMessage = `${message} | ${errors[0].value || errors[0]}`;
      } else if (message) {
        // Simple message from backend
        errorMessage = message;
      } else if (errors) {
        // Only errors array without message
        if (Array.isArray(errors) && errors.length > 0) {
          errorMessage = errors[0].value || errors[0] || 'Validation failed';
        } else if (typeof errors === 'object') {
          // Laravel validation errors as object
          const firstError = Object.values(errors)[0];
          errorMessage = Array.isArray(firstError) ? firstError[0] : firstError;
        }
      }
    } else if (error.message) {
      errorMessage = error.message;
    }
    
    dispatch({
      type: CREATE_PASSWORD_FAIL,
      payload: errorMessage,
    });

    toast.error(errorMessage);
  }
};

export { createPasswordAction };
